import type { CSSProperties } from 'react';

type ThemeColorScheme = 'dark' | 'light';
type PresetControlIntent = 'primary' | 'success' | 'danger';
type ControlIntent = PresetControlIntent | string;
type ControlVariant = 'outline' | string;

const OUTLINE_DISABLED_STYLE_BY_SCHEME: Record<ThemeColorScheme, CSSProperties> = {
  dark: {
    borderColor: 'var(--mantine-color-dark-3)',
    color: 'var(--mantine-color-dark-2)',
    backgroundColor: 'var(--mantine-color-dark-6)',
    opacity: 0.55,
  },
  light: {
    borderColor: 'var(--mantine-color-gray-4)',
    color: 'var(--mantine-color-gray-6)',
    backgroundColor: 'var(--mantine-color-gray-0)',
    opacity: 0.8,
  },
};

const CONTROL_INTENT_COLOR: Record<PresetControlIntent, string | undefined> = {
  primary: undefined,
  success: 'green',
  danger: 'red',
};

export const buttonTextColor = (
  colorScheme: string,
  disabled: boolean
): string => {
  if (disabled) {
    return 'gray';
  }
  return colorScheme === 'dark' ? 'white' : 'black';
};

const isPresetControlIntent = (
  intent: ControlIntent
): intent is PresetControlIntent => {
  return intent === 'primary' || intent === 'success' || intent === 'danger';
};

const resolveControlColor = (intent: ControlIntent): string | undefined => {
  return isPresetControlIntent(intent) ? CONTROL_INTENT_COLOR[intent] : intent;
};

export const disabledOutlineControlStyle = (
  colorScheme: string,
  disabled: boolean
): CSSProperties => {
  if (!disabled) return {};

  return colorScheme === 'dark'
    ? OUTLINE_DISABLED_STYLE_BY_SCHEME.dark
    : OUTLINE_DISABLED_STYLE_BY_SCHEME.light;
};

interface ControlProps {
  variant?: ControlVariant;
  c?: string;
  color?: string;
  style: CSSProperties;
}

interface GetControlPropsOptions {
  colorScheme: string;
  disabled: boolean;
  variant?: ControlVariant;
  style?: CSSProperties;
  intent?: ControlIntent;
}

export const getControlProps = ({
  colorScheme,
  disabled,
  variant,
  style = {},
  intent = 'primary',
}: GetControlPropsOptions): ControlProps => {
  const isOutlineVariant = variant === 'outline';
  const resolvedColor = resolveControlColor(intent);
  const resolvedTextColor =
    isOutlineVariant && intent === 'primary'
      ? buttonTextColor(colorScheme, disabled)
      : undefined;
  const resolvedStyle = isOutlineVariant
    ? {
        ...style,
        ...disabledOutlineControlStyle(colorScheme, disabled),
      }
    : { ...style };

  return {
    variant,
    color: resolvedColor,
    c: resolvedTextColor,
    style: resolvedStyle,
  };
};

export const getOutlineControlProps = (
  colorScheme: string,
  disabled: boolean,
  style: CSSProperties = {},
  intent: ControlIntent = 'primary'
): ControlProps => {
  return getControlProps({
    colorScheme,
    disabled,
    variant: 'outline',
    style,
    intent,
  });
};
